/**
 * CampaignCreatorV2 — Full-screen two-panel campaign creation UI.
 *
 * Left panel:  Config form (name, source_mode, countries multi-select, size, schedule)
 * Right panel: Live flow visualization that changes with source_mode:
 *   EXISTING_DB  → 3-stage standard flow
 *   AI_DISCOVERY → 8-stage AI enrichment pipeline
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Divider,
  FormControl, FormControlLabel, IconButton, InputLabel, ListItemText, MenuItem,
  OutlinedInput, Select, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import arrowBackFill   from '@iconify/icons-eva/arrow-back-fill';
import flashFill       from '@iconify/icons-eva/flash-fill';
import checkmarkCircle from '@iconify/icons-eva/checkmark-circle-2-fill';
import emailFill       from '@iconify/icons-eva/email-fill';
import linkedinFill    from '@iconify/icons-eva/linkedin-fill';
import searchFill      from '@iconify/icons-eva/search-fill';
import peopleFill      from '@iconify/icons-eva/people-fill';
import brainFill       from '@iconify/icons-eva/bulb-fill';
import editFill        from '@iconify/icons-eva/edit-fill';
import globeFill       from '@iconify/icons-eva/globe-fill';
import eyeFill         from '@iconify/icons-eva/eye-fill';
import {
  createCampaign, previewEligibility, getFilterOptions, getOutreachConfig,
  listOutlookAccounts, listUnipileAccounts, listMessageTemplates,
  listActiveUsers, getMySignature,
} from '../../utils/outreachApi';
import { Link as RouterLink } from 'react-router-dom';
import HtmlEditor from './shared/HtmlEditor';
import TimezonePicker from './shared/TimezonePicker';

// ─── Pipeline stage definitions ───────────────────────────────────────────────
// All campaigns (both EXISTING_DB and AI_DISCOVERY) now go through the full
// enrichment + review pipeline before activation.

const PIPELINE_STAGES = [
  { id: 'eligibility', icon: searchFill,      label: 'Eligibility scan',   desc: 'Filter companies: un-outreached, valid, stage 0%/future, matching your rules' },
  { id: 'li_company',  icon: linkedinFill,    label: 'LinkedIn company',   desc: 'Unipile: fetch company LinkedIn page' },
  { id: 'li_founder',  icon: peopleFill,      label: 'Find founder/CEO',   desc: 'Unipile: locate & profile the decision-maker' },
  { id: 'perplexity',  icon: searchFill,      label: 'Web research',       desc: 'Perplexity: press, funding history, CEO signals' },
  { id: 'scoring',     icon: brainFill,       label: 'AI scoring',         desc: 'GPT-4o: qualify + generate personalization hook' },
  { id: 'messages',    icon: editFill,        label: 'Message generation', desc: 'GPT-4o: write 3 tailored outreach messages' },
  { id: 'review',      icon: checkmarkCircle, label: 'Human review',       desc: 'Review queue — approve, edit, or reject each company' },
  { id: 'send',        icon: emailFill,       label: 'Send sequence',      desc: 'Emails via Outlook + LinkedIn invites via Unipile' },
];

const SIZE_OPTIONS = [
  { value: '1-50',    label: 'Startup (1–50)' },
  { value: '50-200',  label: 'SMB (50–200)' },
  { value: '200-500', label: 'Mid-market (200–500)' },
  { value: '500+',    label: 'Enterprise (500+)' },
];

// Full IANA list, falling back to a curated set if the runtime is older.
const TIMEZONE_OPTIONS = (() => {
  try {
    if (typeof Intl?.supportedValuesOf === 'function') {
      return Intl.supportedValuesOf('timeZone');
    }
  } catch (e) { /* fall through */ }
  return [
    'Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Asia/Tokyo', 'Asia/Shanghai',
    'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Moscow',
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'America/Sao_Paulo', 'America/Toronto',
    'Australia/Sydney', 'Australia/Melbourne',
    'UTC',
  ];
})();

// ─── FlowNode (right-panel pipeline visualizer) ────────────────────────────────

function FlowNode({ stage, isLast }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Stack alignItems="center" sx={{ width: 36, flexShrink: 0 }}>
        <Box
          sx={{
            width: 36, height: 36, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: 'primary.lighter',
            border: '2px solid',
            borderColor: 'primary.light',
          }}
        >
          <Icon icon={stage.icon} width={16} style={{ color: '#2065D1' }} />
        </Box>
        {!isLast && (
          <Box sx={{ width: 2, flexGrow: 1, minHeight: 18, bgcolor: 'divider', mt: 0.5 }} />
        )}
      </Stack>
      <Box sx={{ pb: 2 }}>
        <Typography variant="body2" fontWeight={600} color="text.primary">
          {stage.label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {stage.desc}
        </Typography>
      </Box>
    </Stack>
  );
}

function FlowVisualization({ isDemo, eligibleCount, countLoading }) {
  return (
    <Stack spacing={0}>
      {PIPELINE_STAGES.map((stage, i) => {
        const isLast = i === PIPELINE_STAGES.length - 1;
        // Demo: annotate the send step
        const demoStage = (isDemo && stage.id === 'send')
          ? { ...stage, desc: 'Messages drafted & logged — no actual sends (demo mode)', icon: eyeFill }
          : stage;
        return <FlowNode key={stage.id} stage={demoStage} isLast={isLast} />;
      })}

      {/* Count feedback */}
      {countLoading && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
          <CircularProgress size={12} />
          <Typography variant="caption" color="text.secondary">Checking match count…</Typography>
        </Stack>
      )}
      {!countLoading && eligibleCount !== null && (
        <Chip
          label={eligibleCount > 0
            ? `~${eligibleCount.toLocaleString()} companies match your rules`
            : 'No companies match — broaden filters'}
          color={eligibleCount > 0 ? 'primary' : 'warning'}
          size="small"
          sx={{ alignSelf: 'flex-start', mt: 1 }}
        />
      )}
    </Stack>
  );
}

// ─── SourceModeCard — plain clickable card (no FormControlLabel) ───────────────

function SourceModeCard({ value, selected, label, desc, icon, badge, onClick }) {
  return (
    <Box
      onClick={() => onClick(value)}
      sx={{
        p: 2, borderRadius: 2, cursor: 'pointer',
        border: '2px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? (t) => alpha(t.palette.primary.lighter, 0.5) : 'background.paper',
        transition: 'border-color 0.2s, background-color 0.2s',
        '&:hover': { borderColor: 'primary.light' },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 36, height: 36, borderRadius: 1.5, flexShrink: 0,
            bgcolor: selected ? 'primary.main' : 'action.hover',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background-color 0.2s',
          }}
        >
          <Icon icon={icon} width={18} color={selected ? '#fff' : '#919EAB'} />
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" mb={0.25}>
            <Typography variant="body2" fontWeight={700}>{label}</Typography>
            {badge && (
              <Chip label={badge} size="small" color="primary" sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">{desc}</Typography>
        </Box>
        {selected && (
          <Icon icon={checkmarkCircle} width={20} style={{ color: '#2065D1', flexShrink: 0, marginTop: 2 }} />
        )}
      </Stack>
    </Box>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

export default function CampaignCreatorV2() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name:             '',
    description:      '',
    source_mode:      'EXISTING_DB',
    is_demo:          false,
    max_companies:    0,    // 0 = unlimited; useful in demo to cap API spend
    flow_type:        'personalized',  // 'personalized' (AI per-contact) | 'template' (predefined)
    template_messages: {
      email1:          { subject: '', body: '' },
      linkedin_invite: { body: '' },
      email_followup:  { subject: '', body: '' },
    },
    countries:        [],   // multi-select array of country label strings
    size:             '',
    per_pm_daily_cap: 50,
    // Multi-timezone schedule: each row = {timezone, window_start, window_end}.
    // Scheduler picks the earliest valid moment across all zones for each send.
    // Default to the logged-in user's browser timezone so the schedule lines up
    // with their working hours out of the box. Falls back to Asia/Kolkata only
    // if the runtime can't resolve a zone (very old browsers).
    schedule_zones:   [{
      timezone: (() => {
        try {
          return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';
        } catch (e) {
          return 'Asia/Kolkata';
        }
      })(),
      window_start: '09:00',
      window_end:   '18:00',
    }],
    // ─── Decoupled sender/reviewer model ───────────────────────────────
    // Single Outlook account ID used to send all emails for the campaign.
    outlook_connection: null,
    // Single Unipile/LinkedIn account ID used for the whole campaign
    unipile_account:  null,
    // Reviewers (== BAs / managers): users-only, multiple, weighted.
    // Each entry: { user: <id>, weight: <int>, is_active: true }
    reviewers:        [],
    // Test mode: when toggle is on, runs the campaign on a single contact
    // bypassing eligibility filters + Apollo + verification. Real sends fire.
    is_test_mode:     false,
    // Multi-contact list. Each row is one independent demo recipient.
    test_contacts:    [{ company_name: '', first_name: '', last_name: '', email: '', linkedin_url: '', title: '' }],
  });
  const [filterOptions, setFilterOptions] = useState({ countries: [], industries: [] });
  const [outlookAccounts, setOutlookAccounts] = useState([]);
  const [unipileAccounts, setUnipileAccounts] = useState([]);
  // Active users for the reviewer multi-select (independent of sender accounts).
  const [activeUsers, setActiveUsers] = useState([]);
  const [messageTemplates, setMessageTemplates] = useState([]);
  // Admin's saved signature, shown on Step 3 so they preview the full email
  // (body + signature). Each BA's actual signature replaces this at send time.
  const [signaturePreview, setSignaturePreview] = useState({ html: '', configured: false });
  // "Show all" overrides for the country filter on Step 4. BAs and
  // LinkedIn accounts each get their own toggle — the admin might want
  // to widen one list without widening the other. Default false →
  // country filter applies. Test mode + empty country selection also
  // bypass the filter (handled in the refetch effect).
  const [showAllPms,     setShowAllPms]     = useState(false);
  const [showAllUnipile, setShowAllUnipile] = useState(false);
  const [eligibleCount, setEligibleCount] = useState(null);
  const [countLoading, setCountLoading]   = useState(false);
  const [saving, setSaving]               = useState(false);
  const [error, setError]                 = useState('');
  const [saved, setSaved]                 = useState(false);
  // Feature flags from the backend. Default ai_flow_enabled=false so the option
  // is locked until the admin flips it on in Django admin (OutreachConfig).
  const [config, setConfig] = useState({
    ai_flow_enabled:       false,
    test_campaign_enabled: false,
    demo_mode_enabled:     false,
    company_cap_enabled:   false,
  });

  // Load filter options + BA accounts + config once
  useEffect(() => {
    getFilterOptions()
      .then((res) => setFilterOptions(res.data || { countries: [], industries: [] }))
      .catch(() => {});
    // Outlook + Unipile lists are now loaded by a separate effect that
    // reacts to country / test-mode / Show-all changes so the picker stays
    // in sync with the audience step.
    listMessageTemplates()
      .then((res) => setMessageTemplates(res.data || []))
      .catch(() => {});
    listActiveUsers()
      .then((res) => setActiveUsers(res.data || []))
      .catch(() => setActiveUsers([]));
    getMySignature()
      .then((res) => setSignaturePreview({
        html:       res.data?.signature_html || '',
        configured: !!res.data?.profile?.is_configured,
      }))
      .catch(() => setSignaturePreview({ html: '', configured: false }));
    getOutreachConfig()
      .then((res) => {
        setConfig({
          ai_flow_enabled:       false,
          test_campaign_enabled: false,
          demo_mode_enabled:     false,
          company_cap_enabled:   false,
          ...(res.data || {}),
        });
        // If AI is disabled, force the form to template flow regardless of what
        // it was initialized to. This prevents users from saving an AI campaign
        // while the flag is off.
        if (!res.data?.ai_flow_enabled) {
          setForm((f) => ({ ...f, flow_type: 'template' }));
        }
      })
      .catch(() => {});
  }, []);

  // Re-fetch Outlook + Unipile lists whenever the country selection, test
  // mode, or the per-list "Show all" toggle changes. BAs and LinkedIn
  // accounts each pull independently — admin can widen one without the
  // other. Filter is also bypassed in test mode and when no countries
  // have been picked.
  useEffect(() => {
    const idMap = filterOptions.country_id_map || {};
    const resolvedIds = (Array.isArray(form.countries) ? form.countries : [])
      .map((label) => idMap[label])
      .filter((n) => Number.isInteger(n) && n > 0);
    const filterPossible = !form.is_test_mode && resolvedIds.length > 0;

    const pmFilterActive = filterPossible && !showAllPms;
    const liFilterActive = filterPossible && !showAllUnipile;
    const pmFilterIds = pmFilterActive ? resolvedIds : undefined;
    const liFilterIds = liFilterActive ? resolvedIds : undefined;

    listOutlookAccounts(pmFilterIds)
      .then((res) => setOutlookAccounts(res.data || []))
      .catch(() => setOutlookAccounts([]));
    listUnipileAccounts(liFilterIds)
      .then((res) => setUnipileAccounts(res.data || []))
      .catch(() => setUnipileAccounts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    JSON.stringify(form.countries),
    form.is_test_mode,
    showAllPms,
    showAllUnipile,
    JSON.stringify(filterOptions.country_id_map || {}),
  ]);

  // Debounced eligibility count (fires when countries or size changes)
  useEffect(() => {
    if (!form.countries.length && !form.size) {
      setEligibleCount(null);
      return;
    }
    const rules = _buildRules(form);
    if (!rules.length) return;

    setCountLoading(true);
    const timer = setTimeout(() => {
      previewEligibility(rules)
        .then((res) => setEligibleCount(res.data?.count ?? null))
        .catch(() => setEligibleCount(null))
        .finally(() => setCountLoading(false));
    }, 600);
    return () => clearTimeout(timer);
  }, [form.countries, form.size]);

  const set = useCallback((key, val) => setForm((f) => ({ ...f, [key]: val })), []);

  // Toggle a reviewer (user) in/out of the campaign's reviewer pool.
  const toggleReviewer = useCallback((userId) => {
    setForm((f) => {
      const exists = f.reviewers.find((r) => r.user === userId);
      if (exists) {
        return { ...f, reviewers: f.reviewers.filter((r) => r.user !== userId) };
      }
      return { ...f, reviewers: [...f.reviewers, { user: userId, weight: 1, is_active: true }] };
    });
  }, []);

  const setReviewerWeight = useCallback((userId, weight) => {
    setForm((f) => ({
      ...f,
      reviewers: f.reviewers.map((r) =>
        r.user === userId ? { ...r, weight: Math.max(1, Number(weight) || 1) } : r
      ),
    }));
  }, []);

  // ─── Wizard navigation ─────────────────────────────────────────────────
  const [currentStep, setCurrentStep] = useState(0);
  const STEPS = [
    { id: 'basics',    label: 'Basics',           short: 'Campaign basics' },
    { id: 'audience',  label: 'Audience',         short: 'Targeting rules' },
    { id: 'template',  label: 'Templates',        short: 'Message templates' },
    { id: 'accounts',  label: 'Sender accounts',  short: 'Who sends' },
    { id: 'reviewers', label: 'Reviewers',        short: 'Who manages' },
    { id: 'launch',    label: 'Schedule & launch', short: 'Final review' },
  ];

  const stepValidation = (idx) => {
    const f = form;
    switch (idx) {
      case 0: {
        if (!f.name.trim()) return 'Campaign name is required.';
        return null;
      }
      case 1: {
        if (f.is_test_mode) {
          const list = f.test_contacts || [];
          if (list.length === 0) return 'Add at least one test contact.';
          const usable = list.filter((tc) => (tc.email || '').trim() || (tc.linkedin_url || '').trim());
          if (usable.length === 0) return 'Each test contact needs at least an email or a LinkedIn URL.';
          return null;
        }
        if (!f.countries.length) return 'Pick at least one country.';
        return null;
      }
      case 2: {
        // Template messages — only enforced for the template flow.
        if (f.flow_type === 'template') {
          const t = f.template_messages || {};
          if (!(t.email1?.body || '').trim())          return 'Step 1 email body is required.';
          if (!(t.linkedin_invite?.body || '').trim()) return 'LinkedIn invite body is required.';
          if (!(t.email_followup?.body || '').trim()) return 'Step 3 email body is required.';
        }
        return null;
      }
      case 3: {
        // Sender accounts: one Outlook + one LinkedIn account.
        if (!f.outlook_connection) return 'Pick a sender Outlook account.';
        if (!f.unipile_account)    return 'Pick a sender LinkedIn account.';
        return null;
      }
      case 4: {
        // Reviewers: at least one user must be selected to review the queue
        // and manage reply threads. Daily review cap is admin-managed.
        if (!f.reviewers.length) return 'Pick at least one reviewer.';
        return null;
      }
      default: return null;
    }
  };

  const goNext = () => {
    const err = stepValidation(currentStep);
    if (err) { setError(err); return; }
    setError('');
    setCurrentStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const goBack = () => {
    setError('');
    setCurrentStep((s) => Math.max(s - 1, 0));
  };

  const handleSave = async () => {
    // Validate all preceding steps before submitting
    for (let i = 0; i < STEPS.length; i += 1) {
      const err = stepValidation(i);
      if (err) {
        setCurrentStep(i);
        setError(err);
        return;
      }
    }
    if (!form.name.trim()) { setError('Campaign name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        name:             form.name.trim(),
        description:      form.description,
        source_mode:      form.source_mode,
        is_demo:          form.is_demo,
        max_companies:    Number(form.max_companies) || 0,
        flow_type:        form.flow_type,
        template_messages: form.flow_type === 'template'
          ? Object.fromEntries(
              Object.entries(form.template_messages || {}).map(([k, v]) => [
                k,
                // Drop UI-only bookkeeping fields (_template_id, _template_name) before posting
                { subject: v?.subject || '', body: v?.body || '' },
              ])
            )
          : {},
        // Decoupled sender accounts (single Outlook + single LinkedIn).
        outlook_connection: form.outlook_connection || null,
        unipile_account:    form.unipile_account || null,
        // Test mode: send only when the toggle is on. Otherwise an empty object.
        // Backend accepts both list (multi-contact) and legacy dict (single).
        // Filter out empty rows so the bootstrap doesn't try to seed blanks.
        test_contact:     form.is_test_mode
          ? (form.test_contacts || []).filter((tc) => (tc.email || '').trim() || (tc.linkedin_url || '').trim())
          : {},
        // per_pm_daily_cap is no longer sent — backend defaults from
        // OutreachConfig.daily_send_cap_per_pm (admin-managed).
        // First zone seeds the legacy single-zone fields (back-compat for any
        // code path that still reads schedule_window_*). schedule_zones is the
        // source of truth for the multi-tz scheduler.
        schedule_zones:        form.schedule_zones,
        schedule_window_start: form.schedule_zones[0]?.window_start || '09:00',
        schedule_window_end:   form.schedule_zones[0]?.window_end   || '18:00',
        schedule_timezone:     form.schedule_zones[0]?.timezone     || 'Asia/Kolkata',
        schedule_weekdays: ['MON', 'TUE', 'WED', 'THU', 'FRI'],
        rules:     _buildRules(form),
        // Reviewers (decoupled from sender accounts).
        reviewers: form.reviewers,
      };
      const res = await createCampaign(payload);
      setSaved(true);
      setTimeout(() => navigate(`/outreach/campaigns/${res.data.id}`), 700);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to create campaign. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const countryOptions = filterOptions.countries || [];

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 24px)', overflow: 'hidden', bgcolor: 'background.default' }}>

      {/* ── Left panel — form ─────────────────────────────────────────── */}
      <Box
        sx={{
          width: { xs: '100%', md: '55%' },
          display: 'flex',
          flexDirection: 'column',
          borderRight: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton size="small" onClick={() => navigate('/outreach/campaigns')}>
              <Icon icon={arrowBackFill} width={20} />
            </IconButton>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                Step {currentStep + 1} of {STEPS.length}
              </Typography>
              <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                {STEPS[currentStep].short}
              </Typography>
            </Box>
          </Stack>

          {/* Progress strip — clickable to jump back, not forward */}
          <Stack direction="row" spacing={1} mt={1.5}>
            {STEPS.map((s, idx) => {
              const done    = idx < currentStep;
              const active  = idx === currentStep;
              return (
                <Box
                  key={s.id}
                  onClick={() => { if (idx <= currentStep) setCurrentStep(idx); }}
                  sx={{
                    flex: 1, height: 4, borderRadius: 2,
                    bgcolor: done ? 'success.main' : active ? 'primary.main' : 'divider',
                    cursor: idx <= currentStep ? 'pointer' : 'default',
                    transition: 'background-color 0.2s',
                  }}
                />
              );
            })}
          </Stack>
        </Box>

        {/* Scrollable form body */}
        <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 3, py: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {/* ════════════════ STEP 1 — BASICS ════════════════ */}
          {currentStep === 0 && <>
          {/* Name + description */}
          <Stack spacing={2} mb={3}>
            <TextField
              label="Campaign name"
              fullWidth
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. SaaS Q2 Outbound"
              size="small"
            />
            <TextField
              label="Description (optional)"
              fullWidth
              multiline
              rows={2}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              size="small"
            />
          </Stack>

          {/* Flow type selector — personalized AI vs template-based */}
          <Box sx={{ mt: -1, mb: 3, px: 2, py: 1.75, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" fontWeight={700} sx={{ mb: 1.25 }}>
              Outreach flow
            </Typography>
            <Stack direction="row" spacing={1.25}>
              {[
                {
                  value: 'personalized',
                  title: 'Personalized (AI)',
                  blurb: 'Fetch each founder’s recent LinkedIn posts + web research, then have AI write each message individually. Higher quality, higher cost.',
                  disabled: !config.ai_flow_enabled,
                },
                {
                  value: 'template',
                  title: 'Template-based',
                  blurb: 'Same email1 / LinkedIn note / email2 for everyone, with {{first_name}} / {{company_name}} variables. No AI generation, no Perplexity research. Verification still runs.',
                  disabled: false,
                },
              ].map((opt) => {
                const selected = form.flow_type === opt.value;
                const isDisabled = opt.disabled;
                return (
                  <Box
                    key={opt.value}
                    onClick={() => { if (!isDisabled) set('flow_type', opt.value); }}
                    sx={{
                      flex: 1, px: 1.5, py: 1.25, borderRadius: 1.5,
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      opacity:  isDisabled ? 0.55 : 1,
                      pointerEvents: isDisabled ? 'auto' : 'auto',
                      border: '1.5px solid', borderColor: selected ? 'primary.main' : 'divider',
                      bgcolor: selected ? (t) => alpha(t.palette.primary.lighter, 0.4) : 'transparent',
                      position: 'relative',
                      '&:hover': { borderColor: isDisabled ? 'divider' : (selected ? 'primary.main' : 'text.disabled') },
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{
                        width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                        border: '2px solid', borderColor: selected ? 'primary.main' : 'text.disabled',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {selected && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />}
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{opt.title}</Typography>
                      {isDisabled && (
                        <Chip size="small" label="Coming soon" sx={{ height: 18, fontSize: 10, ml: 'auto' }} />
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 2.75 }}>
                      {opt.blurb}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Box>

          </>}{/* END STEP 1 — Basics */}

          {/* ════════════════ STEP 3 — TEMPLATE MESSAGES ════════════════ */}
          {currentStep === 2 && <>
          {form.flow_type === 'template' ? (
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="baseline" justifyContent="space-between" mb={1.5} flexWrap="wrap" gap={1}>
                <Typography variant="body2" fontWeight={700}>Predefined messages</Typography>
                <Button
                  size="small" variant="text" component={RouterLink} to="/outreach/templates" target="_blank"
                  startIcon={<Icon icon={editFill} width={14} />}
                >
                  Manage templates
                </Button>
              </Stack>

              {[
                { key: 'email1',          stepNo: 1, stepLabel: 'Cold intro email',         hasSubject: true,  bodyRows: 5, channel: 'EMAIL' },
                { key: 'linkedin_invite', stepNo: 2, stepLabel: 'LinkedIn connection note', hasSubject: false, bodyRows: 2, channel: 'LINKEDIN_INVITE' },
                { key: 'email_followup',  stepNo: 3, stepLabel: 'Email follow-up',          hasSubject: true,  bodyRows: 4, channel: 'EMAIL' },
              ].map(({ key, stepNo, stepLabel, hasSubject, bodyRows, channel }) => {
                const m = form.template_messages?.[key] || {};
                const onChange = (field) => (e) => setForm((f) => ({
                  ...f,
                  template_messages: {
                    ...f.template_messages,
                    [key]: { ...(f.template_messages?.[key] || {}), [field]: e.target.value },
                  },
                }));
                const availableTemplates = messageTemplates.filter(
                  (t) => t.channel === channel && (t.is_active !== false)
                );
                const onPick = (e) => {
                  const id = e.target.value;
                  if (!id) {
                    onChange('subject')({ target: { value: '' } });
                    onChange('body')({ target: { value: '' } });
                    return;
                  }
                  const tpl = messageTemplates.find((t) => t.id === id);
                  if (!tpl) return;
                  // Prefer HTML body — the send composer auto-detects HTML vs plain.
                  // Fall back to plain-text only when the template has no HTML body.
                  setForm((f) => ({
                    ...f,
                    template_messages: {
                      ...f.template_messages,
                      [key]: {
                        subject: tpl.subject || '',
                        body:    tpl.body_html || tpl.body_plain || '',
                        _template_id: tpl.id,
                        _template_name: tpl.name,
                      },
                    },
                  }));
                };
                return (
                  <Box key={key} sx={{
                    mb: 1.5, p: 1.75, borderRadius: 1.5,
                    border: '1px solid', borderColor: 'divider',
                  }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
                      <Box sx={{
                        width: 26, height: 26, borderRadius: '50%',
                        bgcolor: 'primary.main', color: 'common.white',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 800, fontSize: '0.78rem', flexShrink: 0,
                      }}>{stepNo}</Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        Step {stepNo} — {stepLabel}
                      </Typography>
                    </Stack>

                    {/* Picker: choose from library, or leave blank to write inline */}
                    <Autocomplete
                      size="small"
                      fullWidth
                      options={availableTemplates}
                      getOptionLabel={(opt) => opt?.name || ''}
                      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                      value={availableTemplates.find((t) => t.id === m._template_id) || null}
                      onChange={(_, tpl) => onPick({ target: { value: tpl ? tpl.id : '' } })}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={`Pick a saved template (${availableTemplates.length} available)`}
                          placeholder="Type to search…"
                        />
                      )}
                      sx={{ mb: 1.25 }}
                    />

                    {hasSubject && (
                      <TextField
                        size="small" fullWidth label="Subject"
                        value={m.subject || ''}
                        onChange={onChange('subject')}
                        placeholder="Quick question, {{first_name}}"
                        sx={{ mb: 1.25 }}
                      />
                    )}
                    <HtmlEditor
                      value={m.body || ''}
                      onChange={(html) => onChange('body')({ target: { value: html } })}
                      placeholder={
                        key === 'linkedin_invite'
                          ? 'Hi {{first_name}}, sent you a note about {{company_name}} earlier — would love to connect. — {{pm_name}}'
                          : 'Hi {{first_name}},\n\nQuick note from TGC about {{company_name}}...\n\nBest,\n{{pm_name}}'
                      }
                      minHeight={Math.max(bodyRows * 28, 120)}
                      compact={channel === 'LINKEDIN_INVITE'}
                    />
                    {key === 'linkedin_invite' && (() => {
                      const len  = (m.body || '').length;
                      const over = len > 300;
                      // Substituted variables can grow the message a little; warn early at 280.
                      const warn = !over && len > 280;
                      return (
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block', mt: 0.75, textAlign: 'right',
                            color: over ? 'error.main' : warn ? 'warning.main' : 'text.secondary',
                            fontWeight: over || warn ? 700 : 400,
                          }}
                        >
                          {len} / 300 chars
                          {over && ' — LinkedIn will truncate this at send'}
                        </Typography>
                      );
                    })()}

                    {/* Inline signature preview — only for the two email
                        channels. LinkedIn invites/messages don't carry an
                        email signature. Read-only on purpose — the admin
                        edits their own signature from /signature, and each
                        BA's saved signature replaces this at send time. */}
                    {channel === 'EMAIL' && signaturePreview.html && (
                      <Box
                        sx={{
                          mt: 1.5, pt: 1.5,
                          borderTop: '1px dashed', borderColor: 'divider',
                        }}
                        // Backend-rendered TGC signature HTML; safe because
                        // every dynamic field is _esc()'d server-side.
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: signaturePreview.html }}
                      />
                    )}
                  </Box>
                );
              })}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Variables are filled in per-contact at enrichment time. Companies still go through verification + SaaS check before messages are rendered.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body2">
                Personalized (AI) flow: messages are generated per-contact at enrichment time.
                No templates to configure here.
              </Typography>
            </Box>
          )}

          </>}{/* END STEP 3 — Templates */}

          {/* ════════════════ STEP 6 — SCHEDULE & LAUNCH (demo + cap) ════════════════ */}
          {currentStep === 5 && <>
          {/* Demo mode toggle — gated by OutreachConfig.demo_mode_enabled */}
          {config.demo_mode_enabled && (
          <Box
            sx={{
              mt: -1, mb: 3, px: 2, py: 1.5, borderRadius: 2,
              border: '1px solid',
              borderColor: form.is_demo ? 'warning.light' : 'divider',
              bgcolor: form.is_demo ? (t) => alpha(t.palette.warning.lighter, 0.4) : 'transparent',
              transition: 'all 0.2s',
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Icon icon={eyeFill} width={16} color={form.is_demo ? '#B76E00' : '#919EAB'} />
                  <Typography variant="body2" fontWeight={700} color={form.is_demo ? 'warning.dark' : 'text.primary'}>
                    Demo mode
                  </Typography>
                  {form.is_demo && (
                    <Chip label="ON" size="small" color="warning" sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Run the full pipeline — enrichment, scoring, message generation — but skip actual sends.
                  You'll see exactly what messages would go out for each company.
                </Typography>
              </Box>
              <Switch
                checked={form.is_demo}
                onChange={(e) => set('is_demo', e.target.checked)}
                color="warning"
                size="small"
              />
            </Stack>
          </Box>
          )}

          {/* Company cap — gated by OutreachConfig.company_cap_enabled */}
          {config.company_cap_enabled && (
          <Box sx={{
            mb: 3, px: 2, py: 1.5, borderRadius: 2,
            border: '1px solid', borderColor: 'divider',
          }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={700}>Company cap</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Hard limit on how many eligible <b>companies</b> enter the enrichment pipeline.
                  Set to <b>0</b> for unlimited. Each company may yield multiple contacts —
                  e.g. a cap of 10 can produce 15+ contacts in the review queue if Apollo finds
                  multiple founders per company.
                </Typography>
              </Box>
              <TextField
                size="small"
                type="number"
                value={form.max_companies}
                onChange={(e) => set('max_companies', e.target.value)}
                inputProps={{ min: 0, max: 10000, step: 1 }}
                sx={{ width: 110 }}
                placeholder="0"
                helperText={
                  Number(form.max_companies) > 0
                    ? `Cap: ${form.max_companies} companies`
                    : 'Unlimited'
                }
              />
            </Stack>
          </Box>
          )}

          </>}{/* END step 4 demo+cap block (continues below with schedule) */}

          {/* ════════════════ STEP 2 — AUDIENCE ════════════════ */}
          {currentStep === 1 && <>
          {/* Test-mode toggle — gated by OutreachConfig.test_campaign_enabled.
              When the flag is off, the entire test-campaign UI is hidden and
              form.is_test_mode stays at its false default, so the regular
              targeting filters always render. */}
          {config.test_campaign_enabled && (<>
          <Box sx={{
            mb: 2.5, px: 2, py: 1.5, borderRadius: 2,
            border: '1.5px solid',
            borderColor: form.is_test_mode ? 'error.main' : 'divider',
            bgcolor: form.is_test_mode ? (t) => alpha(t.palette.error.lighter, 0.4) : 'transparent',
          }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" fontWeight={700} color={form.is_test_mode ? 'error.dark' : 'text.primary'}>
                    Test campaign
                  </Typography>
                  {form.is_test_mode && (
                    <Chip label="TEST" size="small" color="error" sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Run on a single contact you specify — bypasses eligibility filter, Apollo founder search, and verification.
                  Real sends fire so you can validate the full email + LinkedIn pipeline.
                </Typography>
              </Box>
              <Switch
                checked={form.is_test_mode}
                onChange={(e) => set('is_test_mode', e.target.checked)}
                color="error"
                size="small"
              />
            </Stack>
          </Box>
          </>)}

          {config.test_campaign_enabled && form.is_test_mode ? (
            <>
              <Divider sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">Test contacts (only these receive messages)</Typography>
              </Divider>

              <Alert severity="warning" sx={{ mb: 2 }}>
                In test mode, country / size / industry filters are <strong>ignored</strong>.
                The campaign sends only to the rows below and nothing else, regardless of any
                other filters. Use your own inbox + LinkedIn so you receive the messages.
              </Alert>

              <Stack spacing={1.5} mb={1}>
                {(form.test_contacts || []).map((tc, idx) => (
                  <Box key={idx} sx={{
                    p: 1.5, borderRadius: 1.5,
                    border: '1px solid', borderColor: 'divider',
                    bgcolor: 'background.paper',
                  }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                        Test contact #{idx + 1}
                      </Typography>
                      <IconButton
                        size="small"
                        disabled={(form.test_contacts || []).length <= 1}
                        onClick={() => {
                          const next = [...(form.test_contacts || [])];
                          next.splice(idx, 1);
                          set('test_contacts', next);
                        }}
                      >
                        <Icon icon="eva:trash-2-outline" width={16} />
                      </IconButton>
                    </Stack>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
                      <TextField size="small" label="Company name"
                        value={tc.company_name}
                        onChange={(e) => {
                          const next = [...form.test_contacts];
                          next[idx] = { ...tc, company_name: e.target.value };
                          set('test_contacts', next);
                        }}
                        placeholder="Test Corp" />
                      <TextField size="small" label="Title"
                        value={tc.title}
                        onChange={(e) => {
                          const next = [...form.test_contacts];
                          next[idx] = { ...tc, title: e.target.value };
                          set('test_contacts', next);
                        }}
                        placeholder="Founder & CEO" />
                      <TextField size="small" label="First name"
                        value={tc.first_name}
                        onChange={(e) => {
                          const next = [...form.test_contacts];
                          next[idx] = { ...tc, first_name: e.target.value };
                          set('test_contacts', next);
                        }} />
                      <TextField size="small" label="Last name"
                        value={tc.last_name}
                        onChange={(e) => {
                          const next = [...form.test_contacts];
                          next[idx] = { ...tc, last_name: e.target.value };
                          set('test_contacts', next);
                        }} />
                      <TextField size="small" label="Email" type="email"
                        value={tc.email}
                        onChange={(e) => {
                          const next = [...form.test_contacts];
                          next[idx] = { ...tc, email: e.target.value };
                          set('test_contacts', next);
                        }}
                        placeholder="you@example.com" sx={{ gridColumn: 'span 2' }} />
                      <TextField size="small" label="LinkedIn URL"
                        value={tc.linkedin_url}
                        onChange={(e) => {
                          const next = [...form.test_contacts];
                          next[idx] = { ...tc, linkedin_url: e.target.value };
                          set('test_contacts', next);
                        }}
                        placeholder="https://linkedin.com/in/your-handle" sx={{ gridColumn: 'span 2' }} />
                    </Box>
                  </Box>
                ))}
              </Stack>

              <Button
                size="small"
                variant="outlined"
                startIcon={<Icon icon="eva:plus-outline" width={14} />}
                onClick={() => set('test_contacts', [
                  ...(form.test_contacts || []),
                  { company_name: '', first_name: '', last_name: '', email: '', linkedin_url: '', title: '' },
                ])}
              >
                Add another test contact
              </Button>
            </>
          ) : <>
          <Divider sx={{ mb: 2.5 }}>
            <Typography variant="caption" color="text.secondary">Targeting filters</Typography>
          </Divider>

          {/* Targeting — country multi-select + size */}
          <Stack spacing={2} mb={3}>
            {/* Country — searchable multi-select via Autocomplete */}
            <Autocomplete
              multiple
              size="small"
              disableCloseOnSelect
              options={countryOptions}
              value={form.countries}
              onChange={(_, val) => set('countries', val)}
              renderInput={(params) => (
                <TextField {...params} label="Countries" placeholder="Type to search countries…" />
              )}
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option}>
                  <Checkbox size="small" checked={selected} style={{ marginRight: 8 }} />
                  {option}
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" size="small" label={option} {...getTagProps({ index })} />
                ))
              }
              ChipProps={{ size: 'small' }}
            />

            {/* Company size — searchable single-select */}
            <Autocomplete
              size="small"
              options={SIZE_OPTIONS}
              getOptionLabel={(opt) => opt.label || ''}
              isOptionEqualToValue={(opt, val) => opt.value === val.value}
              value={SIZE_OPTIONS.find((s) => s.value === form.size) || null}
              onChange={(_, val) => set('size', val ? val.value : '')}
              renderInput={(params) => (
                <TextField {...params} label="Company size" placeholder="Any size" />
              )}
            />

            {/* Live count */}
            {countLoading && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={12} />
                <Typography variant="caption" color="text.secondary">Checking match count…</Typography>
              </Stack>
            )}
            {!countLoading && eligibleCount !== null && (
              <Chip
                color={eligibleCount > 0 ? 'success' : 'warning'}
                variant="outlined"
                size="small"
                label={eligibleCount > 0
                  ? `${eligibleCount.toLocaleString()} companies match`
                  : 'No companies match — broaden your filters'}
                sx={{ alignSelf: 'flex-start' }}
              />
            )}
          </Stack>
          </>}{/* end production-targeting branch */}

          </>}{/* END STEP 2 — Audience */}

          {/* ════════════════ STEP 6 — Schedule (continuation) ════════════════ */}
          {currentStep === 5 && <>
          <Divider sx={{ mb: 2.5 }}>
            <Typography variant="caption" color="text.secondary">Send schedule</Typography>
          </Divider>

          {/* Multi-timezone zones — each row is one local business window */}
          <Stack spacing={1.5} mb={2}>
            {(form.schedule_zones || []).map((zone, idx) => (
              <Stack key={idx} direction="row" spacing={1} alignItems="flex-start">
                <TimezonePicker
                  value={zone.timezone || ''}
                  onChange={(tz) => {
                    const next = [...form.schedule_zones];
                    next[idx] = { ...next[idx], timezone: tz };
                    set('schedule_zones', next);
                  }}
                  showDifference={false}
                  sx={{ flex: 2, minWidth: 240 }}
                />
                <TextField
                  label="Start"
                  type="time"
                  size="small"
                  value={zone.window_start}
                  onChange={(e) => {
                    const next = [...form.schedule_zones];
                    next[idx] = { ...next[idx], window_start: e.target.value };
                    set('schedule_zones', next);
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1, minWidth: 110 }}
                />
                <TextField
                  label="End"
                  type="time"
                  size="small"
                  value={zone.window_end}
                  onChange={(e) => {
                    const next = [...form.schedule_zones];
                    next[idx] = { ...next[idx], window_end: e.target.value };
                    set('schedule_zones', next);
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1, minWidth: 110 }}
                />
                <IconButton
                  size="small"
                  onClick={() => {
                    if (form.schedule_zones.length <= 1) return;
                    set('schedule_zones', form.schedule_zones.filter((_, i) => i !== idx));
                  }}
                  disabled={form.schedule_zones.length <= 1}
                  sx={{ flexShrink: 0, mt: 0.5 }}
                >
                  <Icon icon="eva:trash-2-outline" width={16} />
                </IconButton>
              </Stack>
            ))}
            <Button
              size="small"
              variant="outlined"
              startIcon={<Icon icon="eva:plus-outline" width={14} />}
              onClick={() => set('schedule_zones', [
                ...form.schedule_zones,
                { timezone: 'America/New_York', window_start: '09:00', window_end: '18:00' },
              ])}
              sx={{ alignSelf: 'flex-start' }}
            >
              Add timezone window
            </Button>
          </Stack>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Mon–Fri. Sends fire when the current time falls inside any of the configured
            timezone windows; otherwise the scheduler waits for the next window to open.
            The per-BA daily send cap is managed centrally — see Outreach Config in admin.
          </Typography>
          </>}{/* END STEP 5 schedule */}

          {/* ════════════════ STEP 4 — Sender accounts ════════════════ */}
          {currentStep === 3 && <>
          <Divider sx={{ mt: 3, mb: 2.5 }}>
            <Typography variant="caption" color="text.secondary">Outlook account (sender mailbox)</Typography>
          </Divider>

          {/* BA-only country-filter banner — kept here to scope the visible
              Outlook accounts to the campaign's countries when configured. */}
          {!form.is_test_mode && form.countries.length > 0 && (
            <Alert
              severity={showAllPms ? 'warning' : 'info'}
              sx={{ mb: 1.5, py: 0.5 }}
              action={
                <Button
                  size="small"
                  color={showAllPms ? 'warning' : 'inherit'}
                  onClick={() => setShowAllPms((v) => !v)}
                >
                  {showAllPms ? 'Re-apply country filter' : 'Show all'}
                </Button>
              }
            >
              {showAllPms
                ? 'Showing all Outlook accounts.'
                : `Only Outlook accounts tagged for ${form.countries.join(', ')} are shown.`}
            </Alert>
          )}

          {outlookAccounts.length === 0 ? (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {!form.is_test_mode && form.countries.length > 0 && !showAllPms
                ? <>No Outlook accounts tagged for {form.countries.join(', ')}. Use “Show all” above, or tag the account owner from the <strong>Users</strong> admin.</>
                : <>No active Outlook connections found. Connect one at <strong>/outreach/accounts</strong> first.</>}
            </Alert>
          ) : (
            <Stack spacing={1} mb={1}>
              {outlookAccounts.map((acc) => {
                const selected = form.outlook_connection === acc.id;
                return (
                  <Box
                    key={acc.id}
                    onClick={() => set('outlook_connection', selected ? null : acc.id)}
                    sx={{
                      px: 2, py: 1.25, borderRadius: 2, cursor: 'pointer',
                      border: '2px solid',
                      borderColor: selected ? 'primary.main' : 'divider',
                      bgcolor: selected ? (t) => alpha(t.palette.primary.lighter, 0.35) : 'background.paper',
                      transition: 'border-color 0.15s, background-color 0.15s',
                      '&:hover': { borderColor: 'primary.light' },
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Box sx={{
                        width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                        border: '2px solid',
                        borderColor: selected ? 'primary.main' : 'text.disabled',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {selected && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />}
                      </Box>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {acc.user_name || acc.email}
                        </Typography>
                        {acc.email && acc.user_name && (
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                            {acc.email}
                          </Typography>
                        )}
                      </Box>
                      {selected && <Icon icon={checkmarkCircle} width={18} style={{ color: '#2065D1', flexShrink: 0 }} />}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
          <Typography variant="caption" color="text.secondary">
            One Outlook account is used for all email sends in this campaign. The Outlook
            owner does NOT review messages or manage replies by default — set reviewers on
            the next step. Whether the Outlook owner can also view/reply from My Outreach
            is controlled by a global flag in Django admin.
          </Typography>

          {/* ── LinkedIn account (one per campaign, used for all LinkedIn steps) ─────── */}
          <Divider sx={{ mt: 3, mb: 2.5 }}>
            <Typography variant="caption" color="text.secondary">LinkedIn account</Typography>
          </Divider>

          {/* LinkedIn-only country-filter banner */}
          {!form.is_test_mode && form.countries.length > 0 && (
            <Alert
              severity={showAllUnipile ? 'warning' : 'info'}
              sx={{ mb: 1.5, py: 0.5 }}
              action={
                <Button
                  size="small"
                  color={showAllUnipile ? 'warning' : 'inherit'}
                  onClick={() => setShowAllUnipile((v) => !v)}
                >
                  {showAllUnipile ? 'Re-apply country filter' : 'Show all'}
                </Button>
              }
            >
              {showAllUnipile
                ? 'Showing all LinkedIn accounts.'
                : `Only LinkedIn accounts tagged for ${form.countries.join(', ')} are shown.`}
            </Alert>
          )}

          {unipileAccounts.length === 0 ? (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {!form.is_test_mode && form.countries.length > 0 && !showAllUnipile
                ? <>No LinkedIn accounts tagged for {form.countries.join(', ')}. Use “Show all” above, or tag the account owner from the <strong>Users</strong> admin.</>
                : <>No active Unipile/LinkedIn accounts found. Connect one at <strong>/outreach/accounts</strong> before running enrichment.</>}
            </Alert>
          ) : (
            <Stack spacing={1} mb={1}>
              {unipileAccounts.map((acc) => {
                const selected = form.unipile_account === acc.id;
                return (
                  <Box
                    key={acc.id}
                    onClick={() => set('unipile_account', selected ? null : acc.id)}
                    sx={{
                      px: 2, py: 1.25, borderRadius: 2, cursor: 'pointer',
                      border: '2px solid',
                      borderColor: selected ? 'primary.main' : 'divider',
                      bgcolor: selected ? (t) => alpha(t.palette.primary.lighter, 0.35) : 'background.paper',
                      transition: 'border-color 0.15s, background-color 0.15s',
                      '&:hover': { borderColor: 'primary.light' },
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Box sx={{
                        width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                        border: '2px solid',
                        borderColor: selected ? 'primary.main' : 'text.disabled',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {selected && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />}
                      </Box>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {acc.linkedin_handle || acc.display_name || `Account #${acc.id}`}
                        </Typography>
                        {acc.unipile_account_id && (
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                            ID …{String(acc.unipile_account_id).slice(-8)}
                          </Typography>
                        )}
                      </Box>
                      {selected && <Icon icon={checkmarkCircle} width={18} style={{ color: '#2065D1', flexShrink: 0 }} />}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
          <Typography variant="caption" color="text.secondary">
            One LinkedIn account is used for the whole campaign — enrichment, profile fetches, and
            invite/message sends. Independent of which BA's Outlook is sending the email.
          </Typography>
          </>}{/* END STEP 4 — Sender accounts */}

          {/* ════════════════ STEP 5 — REVIEWERS ════════════════ */}
          {currentStep === 4 && <>
          <Divider sx={{ mt: 3, mb: 2.5 }}>
            <Typography variant="caption" color="text.secondary">Reviewers (approve queue + manage replies)</Typography>
          </Divider>

          <Alert severity="info" sx={{ mb: 2, py: 0.75 }}>
            Reviewers are the campaign's BAs — they approve scored prospects in the review queue
            and manage all incoming reply threads. Companies are round-robined across reviewers
            (weighted) at enrollment. Daily review cap per reviewer is managed centrally — see
            Outreach Config in admin.
          </Alert>

          {activeUsers.length === 0 ? (
            <Alert severity="warning" sx={{ mb: 1 }}>
              No active users found. Create users in the <strong>Users</strong> admin first.
            </Alert>
          ) : (
            <Stack spacing={1} mb={1}>
              {activeUsers.map((u) => {
                const assigned = form.reviewers.find((r) => r.user === u.id);
                return (
                  <Box
                    key={u.id}
                    onClick={() => toggleReviewer(u.id)}
                    sx={{
                      px: 2, py: 1.25, borderRadius: 2, cursor: 'pointer',
                      border: '2px solid',
                      borderColor: assigned ? 'primary.main' : 'divider',
                      bgcolor: assigned ? (t) => alpha(t.palette.primary.lighter, 0.35) : 'background.paper',
                      transition: 'border-color 0.15s, background-color 0.15s',
                      '&:hover': { borderColor: 'primary.light' },
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Checkbox
                        checked={!!assigned}
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleReviewer(u.id)}
                        sx={{ p: 0 }}
                      />
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {u.name || u.email}
                        </Typography>
                        {u.email && u.name && (
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                            {u.email}
                          </Typography>
                        )}
                      </Box>
                      {assigned && (
                        <Stack direction="row" alignItems="center" spacing={0.75} onClick={(e) => e.stopPropagation()}>
                          <Typography variant="caption" color="text.secondary">Weight</Typography>
                          <TextField
                            type="number"
                            size="small"
                            inputProps={{ min: 1, max: 10, style: { width: 40, padding: '4px 6px', textAlign: 'center' } }}
                            value={assigned.weight}
                            onChange={(e) => setReviewerWeight(u.id, e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { height: 28 } }}
                          />
                        </Stack>
                      )}
                      {assigned && (
                        <Icon icon={checkmarkCircle} width={18} style={{ color: '#2065D1', flexShrink: 0 }} />
                      )}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
          {form.reviewers.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              {form.reviewers.length} reviewer{form.reviewers.length > 1 ? 's' : ''} assigned.
              Higher weight = more companies round-robined to that reviewer.
            </Typography>
          )}
          </>}{/* END STEP 5 — Reviewers */}
        </Box>

        {/* Sticky footer — wizard navigation */}
        <Box sx={{
          flexShrink: 0, px: 3, py: 1.75,
          borderTop: '1px solid', borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex', alignItems: 'center', gap: 1.25,
        }}>
          <Button
            size="medium"
            onClick={goBack}
            disabled={currentStep === 0 || saving}
            startIcon={<Icon icon={arrowBackFill} width={16} />}
          >
            Back
          </Button>
          <Box sx={{ flex: 1 }} />
          {currentStep < STEPS.length - 1 ? (
            <Button
              variant="contained" size="medium"
              onClick={goNext}
              endIcon={<Icon icon={arrowBackFill} width={16} style={{ transform: 'rotate(180deg)' }} />}
            >
              Continue
            </Button>
          ) : (
            <Button
              variant="contained" size="medium"
              startIcon={saving
                ? <CircularProgress size={14} color="inherit" />
                : <Icon icon={flashFill} width={16} />}
              onClick={handleSave}
              disabled={saving || saved}
            >
              {saved ? 'Saved!' : 'Create campaign'}
            </Button>
          )}
        </Box>
      </Box>

      {/* ── Right panel — vertical timeline that fills in as steps advance ───── */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          flex: 1,
          bgcolor: (t) => alpha(t.palette.primary.lighter, 0.25),
          overflow: 'hidden',
        }}
      >
        <Stack
          sx={{
            px: 4, pt: 4, pb: 2,
            borderBottom: '1px solid', borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" mb={0.25}>
            <Typography variant="subtitle1" fontWeight={700}>
              Campaign preview
            </Typography>
            {form.is_demo && (
              <Chip label="DEMO" size="small" color="warning" sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Builds as you complete each step. Confirm everything looks right before launching.
          </Typography>
        </Stack>

        <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 4, py: 3 }}>
          {/* Vertical step timeline */}
          {STEPS.map((step, idx) => {
            const done   = idx < currentStep;
            const active = idx === currentStep;
            const isLast = idx === STEPS.length - 1;
            return (
              <Box key={step.id} sx={{ display: 'flex', position: 'relative' }}>
                {/* Node column: circle + vertical connector line */}
                <Box sx={{ position: 'relative', mr: 2, flexShrink: 0 }}>
                  <Box sx={{
                    width: 30, height: 30, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: done ? 'success.main' : active ? 'primary.main' : 'background.paper',
                    border: '2px solid',
                    borderColor: done ? 'success.main' : active ? 'primary.main' : 'divider',
                    color: (done || active) ? 'common.white' : 'text.secondary',
                    fontWeight: 800, fontSize: '0.78rem',
                    zIndex: 1, position: 'relative',
                  }}>
                    {done ? '✓' : idx + 1}
                  </Box>
                  {!isLast && (
                    <Box sx={{
                      position: 'absolute',
                      top: 30, left: 14,
                      width: 2, bottom: -28,
                      bgcolor: done ? 'success.main' : 'divider',
                    }} />
                  )}
                </Box>

                {/* Step content card */}
                <Box sx={{ flex: 1, pb: 3.5, pt: 0.25 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700, lineHeight: 1.2,
                      color: active ? 'primary.main' : done ? 'success.dark' : 'text.disabled',
                    }}
                  >
                    {step.label}
                  </Typography>
                  {(done || active) && (
                    <Box sx={{
                      mt: 1, p: 1.5, borderRadius: 1.5,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: active ? 'primary.light' : 'divider',
                    }}>
                      {idx === 0 && <StepBasicsSummary form={form} />}
                      {idx === 1 && <StepAudienceSummary form={form} eligibleCount={eligibleCount} countLoading={countLoading} />}
                      {idx === 3 && <StepAccountsSummary form={form} outlookAccounts={outlookAccounts} unipileAccounts={unipileAccounts} activeUsers={activeUsers} />}
                      {idx === 5 && <StepLaunchSummary form={form} eligibleCount={eligibleCount} />}
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}

          {/* Once-everything-is-done: pipeline expectation box */}
          {currentStep === STEPS.length - 1 && (
            <Box
              sx={{
                mt: 1, p: 2, borderRadius: 2,
                bgcolor: 'background.paper',
                border: '1px solid', borderColor: form.is_demo ? 'warning.light' : 'divider',
              }}
            >
              <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" mb={0.75}>
                What happens after you click "Create campaign"
              </Typography>
              <Typography variant="caption" color="text.secondary" component="ol" sx={{ pl: 2.5, m: 0, lineHeight: 2 }}>
                <li>Apollo founder search runs for every eligible company.</li>
                <li>Each contact is verified via LinkedIn (employment check).</li>
                <li>Per-contact messages are generated (AI or templates).</li>
                <li>Review queue tab appears — you approve before anything sends.</li>
                {form.is_demo
                  ? <li><strong style={{ color: '#B76E00' }}>Demo mode:</strong> messages drafted &amp; logged, nothing is actually sent.</li>
                  : <li>Approved contacts receive the 3-step sequence via BA Outlook + LinkedIn.</li>
                }
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ─── Step summary sub-components (right-panel timeline) ───────────────────────

function _row(label, value) {
  return (
    <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 0.4 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, minWidth: 78 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ flex: 1, color: 'text.primary' }} noWrap>
        {value || <span style={{ color: '#919EAB' }}>—</span>}
      </Typography>
    </Stack>
  );
}

function StepBasicsSummary({ form }) {
  const flowLabel = form.flow_type === 'template' ? 'Template-based' : 'Personalized (AI)';
  const templates = form.flow_type === 'template'
    ? Object.entries(form.template_messages || {})
        .filter(([, v]) => (v?.body || '').trim())
        .map(([k]) => ({ email1: 'Email 1', linkedin_invite: 'LinkedIn', email_followup: 'Email 2' }[k]))
        .join(', ')
    : null;
  return (
    <Box>
      {_row('Name', form.name || null)}
      {_row('Flow',  <>{flowLabel} <Chip label={form.flow_type} size="small" sx={{ height: 16, fontSize: '0.6rem', ml: 0.5 }} /></>)}
      {form.flow_type === 'template' && _row('Messages', templates || 'none yet')}
      {form.description && _row('About', form.description.slice(0, 80) + (form.description.length > 80 ? '…' : ''))}
    </Box>
  );
}

function StepAudienceSummary({ form, eligibleCount, countLoading }) {
  const countries = (form.countries || []).join(', ') || 'any country';
  const cap = Number(form.max_companies) > 0 ? `${form.max_companies} companies` : 'unlimited';
  return (
    <Box>
      {_row('Countries', countries)}
      {_row('Cap', cap)}
      <Stack direction="row" spacing={1} alignItems="baseline" mt={0.75}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, minWidth: 78 }}>
          Eligible
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main' }}>
          {countLoading
            ? '…'
            : eligibleCount != null
              ? `${eligibleCount.toLocaleString()} in DB`
              : 'add filters to see'}
        </Typography>
      </Stack>
    </Box>
  );
}

function StepAccountsSummary({ form, outlookAccounts, unipileAccounts, activeUsers }) {
  const ol = outlookAccounts.find((a) => a.id === form.outlook_connection);
  const li = unipileAccounts.find((a) => a.id === form.unipile_account);
  const reviewerNames = (form.reviewers || [])
    .map((r) => (activeUsers || []).find((u) => u.id === r.user)?.name)
    .filter(Boolean);
  return (
    <Box>
      {_row('Outlook',  ol ? (ol.email || ol.user_name) : 'none picked')}
      {_row('LinkedIn', li ? (li.linkedin_handle || li.display_name || `Account #${li.id}`) : 'none picked')}
      {li?.daily_action_cap && _row('LI cap', `${li.actions_today ?? 0}/${li.daily_action_cap} today`)}
      {_row('Reviewers', reviewerNames.length
        ? `${reviewerNames.length} × ${reviewerNames[0]}${reviewerNames.length > 1 ? ` + ${reviewerNames.length - 1} more` : ''}`
        : 'none picked')}
    </Box>
  );
}

function StepLaunchSummary({ form, eligibleCount }) {
  const cap = Number(form.max_companies) > 0
    ? Math.min(Number(form.max_companies), eligibleCount ?? Number(form.max_companies))
    : eligibleCount;
  return (
    <Box>
      {_row('Hours', (form.schedule_zones || []).length > 0
        ? form.schedule_zones.map((z) =>
            `${z.window_start || '—'}–${z.window_end || '—'} (${z.timezone || '—'})`
          ).join(' · ')
        : '—')}
      {_row('Pace', 'Daily cap from Outreach Config (admin-managed)')}
      {_row('Mode', form.is_demo
        ? <><Chip label="DEMO" size="small" color="warning" sx={{ height: 16, fontSize: '0.6rem' }} /> nothing actually sends</>
        : 'Live — real sends will go out')}
      {cap != null && _row('Run size', `${cap.toLocaleString()} companies`)}
    </Box>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function _buildRules(form) {
  const rules = [];
  if (form.countries && form.countries.length === 1) {
    rules.push({ field: 'country', operator: 'eq', value: form.countries[0], group: 1 });
  } else if (form.countries && form.countries.length > 1) {
    rules.push({ field: 'country', operator: 'in', value: form.countries, group: 1 });
  }
  if (form.size) {
    const sizeRanges = {
      '1-50':    [1, 50],
      '50-200':  [50, 200],
      '200-500': [200, 500],
      '500+':    [500, 100000],
    };
    const range = sizeRanges[form.size];
    if (range) {
      rules.push({ field: 'fte', operator: 'between', value: range, group: 1 });
    }
  }
  return rules;
}
